import React, { useMemo, useState } from 'react';
import type { RolPermiso } from '../types';
import { PageShell, glassLight, primaryButton, ghostButton, EmptyState, MotionGrid, MotionItem, useIsMobile, usePagination, Pagination, T } from '../ui';
// Módulos actuales del sistema (orden de despliegue). Cualquier permiso viejo
// guardado para módulos que ya no existen (mapa, equipos, empleados, etc.) no
// se muestra.
// Debe mantenerse alineada con MODULE_KEYS de App.tsx: un módulo que falte
// aquí se puede otorgar en el editor de roles pero no se ve en esta matriz.
const MODULOS_ACTUALES = ['inicio', 'consumo', 'costoConsumo', 'sucursales', 'reportes', 'alertas', 'usuarios', 'roles', 'gas', 'pedidos', 'pedidosAutorizar', 'flota', 'auditoria', 'configuracion'] as const;
const moduleLabels: Record<string, string> = {
  inicio: 'Panel',
  consumo: 'Consumo',
  costoConsumo: 'Ver costo estimado',
  sucursales: 'Sucursales',
  reportes: 'Reportes',
  alertas: 'Alertas',
  usuarios: 'Usuarios',
  roles: 'Roles',
  gas: 'Gas Industrial',
  pedidos: 'Órdenes de Pedido',
  pedidosAutorizar: 'Autorizar órdenes',
  flota: 'Flota y Destinos',
  auditoria: 'Auditoría',
  configuracion: 'Configuración'
};
const sinPermiso = {
  leer: false,
  editar: false,
  archivar: false
};
export function RolesPermisos({
  roles,
  onNew,
  onEdit,
  onDelete,
  canArchive
}: {
  roles: RolPermiso[];
  onNew: () => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  canArchive: boolean;
}) {
  const [tab, setTab] = useState<'activos' | 'archivados'>('activos');
  const isMobile = useIsMobile();
  const filteredRoles = useMemo(() => {
    return roles.filter(r => tab === 'activos' ? !r.archivado : r.archivado);
  }, [roles, tab]);
  const {
    page,
    setPage,
    totalPages,
    pageItems,
    rangeStart,
    rangeEnd,
    total
  } = usePagination(filteredRoles);
  return <PageShell title="Roles y permisos" sub="Control de accesos por perfil del sistema" action={<button onClick={onNew} style={primaryButton} data-id="element-1041" data-mp="375bv010k9ysh">
          + Nuevo rol
        </button>} data-id="element-1040" data-mp="377oi0sl6ezce">
      
      <div style={{
      display: 'flex',
      gap: 8,
      marginBottom: 20
    }} data-id="element-1042" data-mp="9e4oe8gm4k9b">
        
        <button onClick={() => {
        setTab('activos');
        setPage(1);
      }} style={{
        ...ghostButton,
        background: tab === 'activos' ? T.accentSoft : 'transparent',
        color: tab === 'activos' ? T.accent : T.textMuted
      }} data-id="element-1043" data-mp="n5i5zguyn81p">
          
          Activos
        </button>
        {canArchive && <button onClick={() => {
        setTab('archivados');
        setPage(1);
      }} style={{
        ...ghostButton,
        background: tab === 'archivados' ? T.accentSoft : 'transparent',
        color: tab === 'archivados' ? T.accent : T.textMuted
      }} data-id="element-1044" data-mp="3ml6dxi8d3f30">
          
            Archivados
          </button>}
      </div>

      {filteredRoles.length === 0 ? <EmptyState title="Sin roles definidos" hint="Crea perfiles para controlar qué puede ver y editar cada usuario." icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" data-id="element-1046" data-mp="1jc4fy8k416fj">
          
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" data-id="element-1047" data-mp="1nf0zq2ldz8l2" />
            </svg>} data-id="element-1045" data-mp="1fw9v5udlywfj" /> : <MotionGrid style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit,minmax(310px,1fr))',
      gap: 14
    }} data-id="element-1048" data-mp="19fhv3jrsafdi">
        
          {pageItems.map(rol => <MotionItem key={rol.id} className="getr-card" style={{
        ...glassLight,
        padding: 20
      }} data-id="element-1049" data-mp="222ccg7tb0ymr">
          
              <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12
        }} data-id="element-1050" data-mp="2715umdjjddt9">
            
                <div style={{
            display: 'flex',
            gap: 12,
            minWidth: 0
          }} data-id="element-1051" data-mp="1xbvzspxb5h1g">
              
                  <div style={{
              width: 38,
              height: 38,
              borderRadius: 11,
              flexShrink: 0,
              background: T.accentSoft,
              border: `1px solid ${T.accentBorder}`,
              color: T.accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }} data-id="element-1052" data-mp="2l16qfeky1v1v">
                
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" data-id="element-1053" data-mp="s6qna9jj674g">
                  
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" data-id="element-1054" data-mp="1kumif9tgbqz4" />
                    </svg>
                  </div>
                  <div style={{
              minWidth: 0
            }} data-id="element-1055" data-mp="1fwl3n1326dtg">
                
                    <div style={{
                fontSize: 16,
                fontWeight: 700,
                color: T.text
              }} data-id="element-1056" data-mp="1b38orio6lldw">
                  
                      {rol.nombre}
                    </div>
                    {rol.descripcion ? <div style={{
                marginTop: 5,
                color: T.textSecondary,
                fontSize: 13
              }} data-id="element-1057" data-mp="23mnsz9fl95od">
                      {rol.descripcion}
                    </div> : null}
                  </div>
                </div>
                <div style={{
            display: 'flex',
            gap: 8,
            flexShrink: 0,
            justifyContent: isMobile ? 'stretch' : 'flex-end'
          }} data-id="element-1058" data-mp="2b4vlghkednns">
              
                  <button className="getr-btn-ghost" onClick={() => onEdit(rol.id)} style={{
              ...ghostButton,
              flex: isMobile ? 1 : 'none'
            }} data-id="element-1059" data-mp="2l44dv5zm6u81">
                
                    Editar
                  </button>
                  <button onClick={() => onDelete(rol.id)} style={{
              ...ghostButton,
              flex: isMobile ? 1 : 'none',
              color: rol.archivado ? T.info : T.warn,
              borderColor: rol.archivado ? 'rgba(96,165,250,0.3)' : 'rgba(251,191,36,0.3)',
              background: rol.archivado ? 'rgba(96,165,250,0.1)' : 'rgba(251,191,36,0.1)'
            }} data-id="element-1060" data-mp="2yu7r57gindlc">
                
                    {rol.archivado ? 'Desarchivar' : 'Archivar'}
                  </button>
                </div>
              </div>
              <div style={{
          marginTop: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }} data-id="element-1061" data-mp="18rfn82cqvqyw">
            
                {MODULOS_ACTUALES.map(modulo => {
            const acciones = rol.permisos[modulo] ?? sinPermiso;
            return <div key={modulo} style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'minmax(0,1fr) repeat(3,46px)' : 'minmax(0,1fr) repeat(3,60px)',
              alignItems: 'center',
              gap: isMobile ? 5 : 8,
              padding: '8px 0',
              borderTop: `1px solid ${T.border}`
            }} data-id="element-1062" data-mp="1wenq6dmpanxr">
                  
                      <span style={{
                color: T.textSecondary,
                fontSize: 13,
                fontWeight: 600
              }} data-id="element-1063" data-mp="b1uk5vawe9kd">
                    
                        {moduleLabels[modulo] || modulo}
                      </span>
                      {(['leer', 'editar', 'archivar'] as const).map(accion => <span key={accion} title={accion} style={{
                textAlign: 'center',
                padding: isMobile ? '5px 3px' : '5px 7px',
                borderRadius: T.rSm,
                background: acciones[accion] ? T.accentSoft : 'rgba(255,255,255,.04)',
                border: acciones[accion] ? `1px solid ${T.accentBorder}` : `1px solid ${T.border}`,
                color: acciones[accion] ? T.accent : T.textMuted,
                fontSize: isMobile ? 9.5 : 11,
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }} data-id="element-1064" data-mp="x4a125cbz52">
                      
                            {isMobile ? {
                  leer: 'Ver',
                  editar: 'Edit',
                  archivar: 'Arch'
                }[accion] : accion}
                          </span>)}
                    </div>;
          })}
              </div>
            </MotionItem>)}
        </MotionGrid>}
      {roles.length > 0 && <Pagination page={page} totalPages={totalPages} onChange={setPage} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="roles" data-id="element-1065" data-mp="276wqbzkxni7b" />}

    </PageShell>;
}